import { css, html, LitElement, unsafeCSS, nothing } from 'lit';
import styles from './fileUploader.scss?inline';
import { ref, createRef } from 'lit/directives/ref.js';
import { ATTRIBUTES, BYTES_IN_KB, BYTES_IN_MB } from './constants';
import messages from './localization.json';
import { getMessage } from '@/localization.js';
import { map } from 'lit/directives/map.js';
import { FormValidationController } from '@/controllers/FormValidationController.js';
import { checkValidity } from '@/components/input/common.js';

export default class SotbitFileUploader extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static formAssociated = true;

    static properties = {
        label: { type: String },
        name: { type: String },
        disabled: { type: Boolean },
        required: { type: Boolean, reflect: true },
        multiple: { type: Boolean },
        error: { type: Boolean },
        errorText: { type: String, attribute: ATTRIBUTES.ERROR_TEXT },
        inputCaption: { type: String, attribute: ATTRIBUTES.INPUT_CAPTION },
        maxSize: { type: String, attribute: ATTRIBUTES.MAX_SIZE },
        value: { type: Object },
        accept: { type: String },
    };

    constructor() {
        super();

        this._internals = this.attachInternals();
        this._validationController = new FormValidationController(this, {
            customValidate: () => this._syncConstraintValidity(),
        });
        this.form = null;
        this.label = '';
        this.name = 'file';
        this.disabled = false;
        this.required = false;
        this.inputCaption = getMessage(messages, 'selectFile');
        this.maxSize = '';
        this.multiple = false;
        this._hiddenInputRef = createRef();
        const dt = new DataTransfer();
        this._files = dt.files;
        this._value = '';
    }

    connectedCallback() {
        super.connectedCallback();
        if (!this.hasAttribute('tabindex')) {
            this.setAttribute('tabindex', '-1');
        }
    }

    disconnectedCallback() {
        this._validationController?.removeInputHandlers();
        super.disconnectedCallback();
    }

    formAssociatedCallback(form) {
        this._validationController.removeInputHandlers();
        this.updateFormValue();
        this.form = form;
        this._validationController.validate();
        this._validationController.setInputHandlers();
    }

    /**
     * @param {boolean} [reportValidity]
     * @returns {boolean}
     */
    checkValidity(reportValidity) {
        return checkValidity(this, reportValidity);
    }

    reportValidity() {
        return this.checkValidity(true);
    }

    /**
     * Native form validation calls focus() on an invalid control.
     * The internal input[type=file] is hidden and not focusable - focus is transferred to the file selection button.
     */
    focus(options) {
        const btn = this.renderRoot?.querySelector('sotbit-button');
        if (btn && typeof btn.focus === 'function') {
            btn.focus(options);
            return;
        }
        super.focus(options);
    }

    get type() {
        return 'file';
    }

    get value() {
        return this._hiddenInputRef?.value ? this._hiddenInputRef?.value.value : this._value;
    }

    set value(value) {
        this._value = value;

        if (this._hiddenInputRef?.value) {
            this._hiddenInputRef.value.value = value;
        }
    }

    get files() {
        return this._hiddenInputRef?.value ? this._hiddenInputRef.value.files : this._files;
    }

    set files(files) {
        if (Array.isArray(files)) {
            const dt = new DataTransfer();
            files.forEach((file) => dt.items.add(file));
            files = dt.files;
        }

        this._files = files;

        if (this._hiddenInputRef?.value) {
            this._hiddenInputRef.value.files = files;
        }

        if (!this._files.length) {
            this.value = '';
        }
    }

    /**
     * Clears validation UI. Must remove DOM attributes: {@link FormValidationController#setValidateState}
     * sets `error` / `error-text` via `toggleAttribute` / `setAttribute`, which do not sync Lit properties
     * when the attribute is already present — so only assigning `this.error = false` leaves stale attributes
     * and repeat validation will not fire `attributeChangedCallback` or update the template.
     */
    resetErrorState() {
        this.removeAttribute('error');
        this.removeAttribute(ATTRIBUTES.ERROR_TEXT);
        this.error = false;
        this.errorText = '';
    }

    formResetCallback() {
        const dt = new DataTransfer();
        this.files = dt.files;
        this._internals.setFormValue(null);
        this._internals.setValidity({});
        this.resetErrorState();

        if (this._hiddenInputRef.value) {
            this._hiddenInputRef.value.value = '';
        }

        this.requestUpdate();
    }

    updateFormValue() {
        if (this.multiple) {
            const formData = new FormData();
            Array.from(this.files).forEach((file) => {
                formData.append(this.name, file);
            });
            this._internals.setFormValue(formData);
        } else {
            const file = this.files[0] || null;
            this._internals.setFormValue(file);
        }
    }

    handleFileChange() {
        this.resetErrorState();
        this.updateFormValue();
        this._syncConstraintValidity();
        this.requestUpdate();
    }

    handleDelete(fileToRemove) {
        const dt = new DataTransfer();

        for (let i = 0; i < this.files.length; i++) {
            const file = this.files[i];
            if (file !== fileToRemove) {
                dt.items.add(file);
            }
        }

        this.files = dt.files;
        this.resetErrorState();
        this.updateFormValue();
        this._syncConstraintValidity();
        this.requestUpdate();
    }

    formatSize(bytes) {
        if (bytes < BYTES_IN_KB) {
            return `${bytes} ${getMessage(messages, 'byte')}`;
        }

        if (bytes < BYTES_IN_MB) {
            return `${(bytes / BYTES_IN_KB).toFixed(1)} ${getMessage(messages, 'kbyte')}`;
        }

        return `${(bytes / BYTES_IN_MB).toFixed(1)} ${getMessage(messages, 'mbyte')}`;
    }

    /** @returns {number | null} limit in bytes or null if no limit is specified */
    _getMaxSizeBytes() {
        const maxSize = Number(this.maxSize);
        return Number.isFinite(maxSize) && maxSize > 0 ? maxSize : null;
    }

    /**
     * Applies `required` and `maxSize` rules to `ElementInternals` (used by {@link FormValidationController}).
     */
    _syncConstraintValidity() {
        if (this.disabled) {
            this._internals.setValidity({});
            return;
        }

        const empty = !this.files?.length;

        if (this.required && empty) {
            const msg = getMessage(messages, 'requiredFile');
            this._internals.setValidity({ valueMissing: true }, msg);
            return;
        }

        if (empty) {
            this._internals.setValidity({});
            return;
        }

        const maxBytes = this._getMaxSizeBytes();
        // `customError` is reserved for max-size violations so the UI can hide the summary line (see `_isMaxSizeConstraintError`).
        if (maxBytes !== null && Array.from(this.files).some((f) => f.size > maxBytes)) {
            const msg = getMessage(messages, 'maxFileSize').replace('#SIZE#', this.formatSize(maxBytes));
            this._internals.setValidity({ customError: true }, msg);
            return;
        }

        this._internals.setValidity({});
    }

    updated(changedProperties) {
        super.updated(changedProperties);
        if (
            changedProperties.has('maxSize') ||
            changedProperties.has('disabled') ||
            changedProperties.has('required')
        ) {
            this._syncConstraintValidity();
        }
    }

    _triggerFileDialog() {
        if (this._hiddenInputRef.value && !this.disabled) {
            this._hiddenInputRef.value.click();
        }
    }

    /**
     * `maxSize` failures set only `customError` on internals; the summary area error line is skipped
     * so the message appears on each oversized file row instead.
     */
    _isMaxSizeConstraintError() {
        return Boolean(this._internals?.validity?.customError);
    }

    render() {
        const maxBytes = this._getMaxSizeBytes();
        return html`
            <div class="file-uploader">
                ${this.label ? html`<p class="file-uploader__title">${this.label}</p>` : ''}
                <div class="file-uploader__input-area">
                    <sotbit-button
                        variant="secondary"
                        background
                        outline
                        inline
                        @click=${this._triggerFileDialog.bind(this)}
                        ?disabled=${this.disabled}
                    >
                        <span slot="content">${this.inputCaption}</span>
                    </sotbit-button>
                    <input
                        type="file"
                        accept=${this.accept || nothing}
                        ?disabled=${this.disabled}
                        ?multiple=${this.multiple}
                        @change=${this.handleFileChange}
                        style="display: none"
                        ${ref(this._hiddenInputRef)}
                    />
                    ${this.error && this.errorText && !this._isMaxSizeConstraintError()
                        ? html`<p class="file-uploader__error">${this.errorText}</p>`
                        : ''}
                </div>

                ${this.files.length
                    ? html`
                          <div class="file-uploader__list">
                              ${map(Array.from(this.files), (file) => {
                                  const isImage = file.raw?.type?.startsWith('image/');
                                  return html`
                                      <div class="file-item">
                                          ${isImage
                                              ? html`<img
                                                    class="file-item__preview"
                                                    src=${file.url}
                                                    alt=${file.name}
                                                />`
                                              : html`
                                                    <div class="file-item__icon">
                                                        <sotbit-icon icon="docs"></sotbit-icon>
                                                    </div>
                                                `}
                                          <div class="file-item__content">
                                              <span class="file-item__name">${file.name}</span>
                                              <span class="file-item__size">${this.formatSize(file.size || 0)}</span>
                                              ${maxBytes !== null && file.size > maxBytes && this.error
                                                  ? html`
                                                          <div class="file-item__error">
                                                            ${getMessage(messages, 'maxFileSize')
                                                                    .replace('#SIZE#', this.formatSize(maxBytes))}
                                                          </div>`
                                                      : ''}
                                          </div>
                                          <sotbit-button-icon
                                              variant="secondary"
                                              size="s"
                                              background
                                              @click=${() => this.handleDelete(file)}
                                          >
                                              <sotbit-icon slot="content" icon="close" no-padding></sotbit-icon>
                                          </sotbit-button-icon>
                                      </div>
                                  `;
                              })}
                          </div>
                      `
                    : ''}
                <slot name="description"></slot>
            </div>
        `;
    }
}
